/* eslint-disable camelcase */
import { cx } from 'emotion';
import {
  compose, identity, keys, mapObjIndexed, pick,
  pickBy, prop, reduce, values,
} from 'ramda';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Icon from '@ezugi/primitives/src/Icon/Icon';
import Menu from '@ezugi/ui/components/Menu/Menu';
import { DeviceInfo } from '@ezugi/utils';

import { sendAnalytics } from '../../../../analytics/sendAnalytics';
import favoriteIcon from '../../assets/icons/favorite.svg';
import { isFavorite } from '../../components/MiniLobby/utils';
import { useIntl } from '../../hooks/useIntl';
import { lobbyActions, menuActions, tipsActions } from '../../store/actions';
import {
  CASHIER_SOURCE,
  GAME_MENU_LIMITS_PAYOUTS,
  GAME_MENU_PLAYER_HISTORY_CLICK,
  GAMES_CHAT_CLICKED,
  GAMES_GAME_INFO,
  GAMES_SUPPORT_CHAT,
} from '../../store/constants/analytics';
import { SUPPORT_ROOM_KEY, GAME_ROOM_KEY } from '../../store/constants/chat';
import { readMessage } from '../../store/reducers/chat';
import {
  currencySelector,
  currentLimitsSelector,
  nameBeforeGreetingSelector,
  operatorLogoSelector,
  roundIdSelector,
  tableIdSelector,
  userNicknameSelector, widgetsSelector,
  tipsHeaderSelector,
} from '../../store/selectors';
import { unreadSupportMessagesCountSelector } from '../../store/selectors/chat';
import { lobbyAuthGroupsSelector } from '../../store/selectors/lobby';
import { menuOpenSelector } from '../../store/selectors/menu';
import {
  ADD_TO_FAVORITES_KEY,
  REMOVE_FROM_FAVORITES_KEY,
  TIPS_KEY,
  TOGGLE_FAVORITE_KEY,
  actionNames,
  desktopLinks as DESKTOP_LINKS, linksMap,
  localeKeys, mobileLinks,
} from './constants';
import {
  iconWithBadgeClassName, defaultStyle, addToFavoriteClassName,
  mobileStyle,
} from './style.css';

const NOOP = () => { };
const desktopMenuStyle = {
  showHeader: true,
  height: 'auto',
  style: {
    maxHeight: '70%',
  },
};
const pickEnabled = pickBy(identity);
const getLinks = compose(
  reduce((acc, x) => ({ ...acc, [x]: linksMap[x] }), {}),
  keys,
  pickEnabled
);

const mapLinkLabels = (locale) => (value, key) => ({
  ...value,
  label: prop(key, locale),
});
const mapLinkHandlers = (props) => ({ onClick: _onClick, ...value }, key) => ({
  ...value,
  onClick: () => {
    props.onLinkClick(value);
    const action = props[prop(key, actionNames)] || NOOP;
    action();
  },
});

function GameMenu() {
  const dispatch = useDispatch();
  const widgets = useSelector(widgetsSelector);
  const logo = useSelector(operatorLogoSelector);
  const nickname = useSelector(userNicknameSelector);
  const nameBeforeGreeting = useSelector(nameBeforeGreetingSelector);
  const open = useSelector(menuOpenSelector);
  const unreadSupportMessagesCount = useSelector(unreadSupportMessagesCountSelector);

  const locales = useIntl({ keys: localeKeys });

  const tableId = useSelector(tableIdSelector);
  const tipsHeader = useSelector(tipsHeaderSelector);
  const currency = useSelector(currencySelector);
  const limits = useSelector(currentLimitsSelector);
  const roundId = useSelector(roundIdSelector);
  const authGroups = useSelector(lobbyAuthGroupsSelector);

  const favorite = isFavorite(tableId, authGroups);

  const desktopLinks = useMemo(
    () => (!tipsHeader
      ? DESKTOP_LINKS
      : DESKTOP_LINKS.filter((link) => link !== TIPS_KEY)),
    [ tipsHeader ]
  );

  const onLinkClick = useCallback(
    (nextWidget) => {
      dispatch(menuActions.menu.set({ widget: nextWidget, open: !!nextWidget.keepMenu }));
    },
    [ dispatch ]
  );

  const handleToggleMenu = useCallback(() => {
    dispatch(menuActions.menu.toggle());
  }, [ dispatch ]);

  const iconClassNames = useMemo(() => ({
    liveSupport: unreadSupportMessagesCount ? iconWithBadgeClassName : undefined,
  }), [ unreadSupportMessagesCount ]);

  const mapClassNames = useCallback((value, key) => ({
    ...value,
    icon: {
      ...(typeof value.icon === 'string' ? { svg: value.icon } : value.icon),
      className: iconClassNames[key],
    },
  }), [ iconClassNames ]);

  const links = useMemo(
    () => {
      const result = compose(
        values,
        mapObjIndexed(mapClassNames),
        mapObjIndexed(
          mapLinkHandlers({
            widgets,
            onLinkClick,
            tipsRequest: () => dispatch(tipsActions.tips.toggleRequest()),
            cashierRequest: () => dispatch(menuActions.cashier.request({ source: CASHIER_SOURCE.MENU })),
            readSupportMessages: () => {
              dispatch(readMessage(SUPPORT_ROOM_KEY));
              sendAnalytics({ event: GAMES_SUPPORT_CHAT, fromMenu: true });
            },
            readRoomMessages: () => {
              dispatch(readMessage(GAME_ROOM_KEY));
              sendAnalytics({ event: GAMES_CHAT_CLICKED, fromMenu: true });
            },
            openMinilobbyWidget: () => dispatch(lobbyActions.lobby.openMenuWidget()),
            sendGameInfoAnalytics: () => sendAnalytics({ event: GAMES_GAME_INFO }),
            toggleFavorite: () => dispatch(lobbyActions.lobby.toggleFavorite({ id: tableId, favorite })),
            homeRequest: () => dispatch(menuActions.home.request()),
            responsibleGamingRequest: () => dispatch(menuActions.responsibleGaming.request()),
            sendLimitsAndPayoutsAnalytics: () => {
              sendAnalytics({
                event: GAME_MENU_LIMITS_PAYOUTS,
                currency,
                min_limit: limits.Min_Bet,
                max_limit: limits.Max_Bet,
                roundId,
              });
            },
            sendPlayerHistoryAnalytics: () => {
              sendAnalytics({
                event: GAME_MENU_PLAYER_HISTORY_CLICK,
                currency,
                min_limit: limits.Min_Bet,
                max_limit: limits.Max_Bet,
                roundId,
                origin: 'game',
              });
            },
          })
        ),
        mapObjIndexed(mapLinkLabels(locales)),
        pick(DeviceInfo.isHandheld ? mobileLinks : desktopLinks),
      )(getLinks(widgets));

      return result.map((link) => {
        if (link.key === TOGGLE_FAVORITE_KEY) {
          link.label = prop(favorite ? REMOVE_FROM_FAVORITES_KEY : ADD_TO_FAVORITES_KEY, locales);
          link.icon = (
            <Icon size={20} svg={favoriteIcon} className={cx({ [addToFavoriteClassName]: !favorite })} />
          );
        }

        return link;
      });
    },
    [
      widgets,
      onLinkClick,
      locales,
      dispatch,
      favorite,
      tableId,
      mapClassNames,
      desktopLinks,
      currency,
      limits,
      roundId,
    ]
  );

  return (
    <Menu
      drawerClassName={cx(defaultStyle, {
        [mobileStyle]: DeviceInfo.isHandheld,
      })}
      {...(!DeviceInfo.isHandheld && desktopMenuStyle)}
      open={open}
      links={links}
      nickname={nickname}
      greeting={locales.welcome}
      nameBeforeGreeting={nameBeforeGreeting}
      logo={logo}
      onClose={handleToggleMenu}
      onBackdropClick={handleToggleMenu}
    />
  );
}

export default GameMenu;
